import React, { Component } from 'react';
import * as PropTypes from 'prop-types';
import {
  Button,
  ListGroup,
  Pagination,
  OverlayTrigger,
  Popover,
} from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSync } from '@whitebox-learning/fontawesome-pro-light-svg-icons';
import MenuTitle from './MenuTitle';
import OpenConceptRevisionCard from './OpenConceptRevisionCard';
import ConceptListItem from './ConceptListItem';
import { Loading } from '../Loading/Loading';
import { HelpIcon } from '../Common/Icons';

class OpenConceptMenu extends Component {
  constructor(props) {
    super(props);

    this.state = {
      selectedConceptName: null,
      revisionsPage: 1,
    };
  }

  drillDown = (conceptName) => {
    this.setState({
      selectedConceptName: conceptName,
      revisionsPage: 1,
    });
  };

  getSelectedConcept = () => {
    return this.props.concepts.find(
      (c) => c.name === this.state.selectedConceptName
    );
  };

  renderRevisionsPagination(revisions) {
    if (revisions.length - 1 > this.props.revisionsPerPage) {
      const pages = Math.ceil(
        (revisions.length - 1) / this.props.revisionsPerPage
      );

      return (
        <Pagination size="sm">
          {[...Array(pages).keys()].map((index) =>
            ((i) => (
              <Pagination.Item
                key={i}
                active={i === this.state.revisionsPage}
                onClick={(e) =>
                  this.setState({ revisionsPage: parseInt(e.target.text) })
                }
              >
                {i}
              </Pagination.Item>
            ))(++index)
          )}
        </Pagination>
      );
    } else {
      return null;
    }
  }

  renderConceptRevisions() {
    const c = this.getSelectedConcept();
    const revisions = Array.prototype.slice
      .call(c.revisions)
      .sort((a, b) => (a.id < b.id ? 1 : -1));
    const recent = revisions[0];
    return (
      <>
        <h3 className="text-center">{c.name}</h3>
        <h4>Most Recent Revision</h4>
        <OpenConceptRevisionCard
          revision={recent}
          isLoading={this.props.loadingFile}
          openRevision={() => this.props.openRevision(recent.id, recent.name)}
        />
        {revisions.length > 1 ? this.renderOlderRevisions(revisions) : null}
      </>
    );
  }

  renderOlderRevisions(revisions) {
    const limit = this.props.revisionsPerPage;
    const start = (this.state.revisionsPage - 1) * limit + 1;

    return (
      <>
        <h6>Older Revisions</h6>
        <ListGroup style={{ overflowY: 'auto' }}>
          {revisions.slice(start, start + limit).map((r) => (
            <OpenConceptRevisionCard
              key={r.id}
              revision={r}
              isLoading={this.props.loadingFile}
              openRevision={() => this.props.openRevision(r.id, r.name)}
            />
          ))}
        </ListGroup>
        <div className="text-center">
          {this.renderRevisionsPagination(revisions)}
        </div>
      </>
    );
  }

  renderConceptList() {
    const currentSpecificationId = this.props.currentSpecificationId;
    const concepts = Array.prototype.slice
      .call(this.props.concepts)
      .filter(
        (c) =>
          currentSpecificationId === 0 ||
          c.specificationId === currentSpecificationId
      )
      .filter((c) => {
        return !c.isShared || c.revisions[0].studentId === this.props.studentId;
      })
      .sort((a, b) => (a.lastModified < b.lastModified ? 1 : -1));
    if (concepts.length === 0) {
      return (
        <p className="text-muted">
          You haven't saved any design concepts for the current specification
          yet.
        </p>
      );
    }

    return (
      <ListGroup>
        {concepts.map((c) => (
          <ConceptListItem
            key={c.name}
            concept={c}
            handleClick={(c) => this.drillDown(c.name)}
          />
        ))}
      </ListGroup>
    );
  }

  render() {
    let back = this.props.back;
    let collection;

    if (this.state.selectedConceptName !== null) {
      back = () => this.drillDown(null);
      collection = this.renderConceptRevisions();
    } else {
      collection = this.renderConceptList();
    }

    const hasShared =
      this.props.isTeacher &&
      this.props.concepts.reduce((acc, v) => acc || v.isShared, false);

    return (
      <div className="fm-simple-panel">
        <MenuTitle
          back={back}
          close={this.props.close}
          extraButtons={
            <Button
              variant="secondary"
              onClick={this.props.loadConcepts}
              title="Refresh Design List"
            >
              <FontAwesomeIcon icon={faSync} spin={this.props.loadingList} />
            </Button>
          }
        >
          Open
        </MenuTitle>
        <div className="fm-scroll">
          {this.props.loadingList ? <Loading /> : null}
          <p className="text-center mb-0">
            Designs from current specifications (
            {this.props.currentSpecificationName})
          </p>

          {hasShared && (
            <div class="alert alert-info mt-3">
              <b>Note:</b> Editing a shared design will not update student
              designs that have forked it. Students will need to fork it again
              to get your changes.
            </div>
          )}

          <div className="text-center mb-3">
            <OverlayTrigger
              trigger="click"
              placement="bottom"
              overlay={
                <Popover id="open-concept-menu-help">
                  <Popover.Header>Where are my designs?</Popover.Header>
                  <Popover.Body>
                    Only designs from the current specification can be opened
                    for editing. However, you can "fork" any existing design
                    concept to start a new concept for the current specification
                    based on the latest revision of the existing design.
                  </Popover.Body>
                </Popover>
              }
            >
              <Button size="sm" variant="link" className="ms-1 p-0">
                <HelpIcon /> Missing designs?
              </Button>
            </OverlayTrigger>
          </div>

          {collection}
        </div>
      </div>
    );
  }
}

OpenConceptMenu.propTypes = {
  concepts: PropTypes.array.isRequired,
  back: PropTypes.func.isRequired,
  close: PropTypes.func.isRequired,
  openRevision: PropTypes.func.isRequired,
  loadingList: PropTypes.bool,
  loadingFile: PropTypes.bool,
  revisionsPerPage: PropTypes.number,
};

OpenConceptMenu.defaultProps = {
  concepts: [],
  loadingList: true,
  loadingFile: false,
  revisionsPerPage: 5,
};

export default OpenConceptMenu;
